import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";
import { useQuote } from "@/context/QuoteContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuoteModal } = useQuote();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-medis-silver/25 shadow-sm"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <a href="#top" className="flex items-center gap-3" data-testid="nav-logo-link">
            <img src="/logo.jpg" alt="MEDIS Group LLP" className="h-14 w-14 object-contain" />
            <span className="font-display font-bold text-[15px] text-medis-blue tracking-tight">
              MEDIS Group LLP
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="link-underline text-[13.5px] font-medium text-medis-navy/85 hover:text-medis-blue transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => openQuoteModal()}
              className="hidden lg:inline-flex items-center gap-2 bg-medis-blue text-white pl-5 pr-4 py-2.5 rounded-md text-[13.5px] font-medium hover:bg-medis-blue-dark transition-colors group"
              data-testid="nav-cta-quote"
            >
              Get a Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              className="lg:hidden p-2 text-medis-navy"
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-medis-silver/25" data-testid="nav-mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-[15px] font-medium text-medis-navy/90 hover:text-medis-blue"
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                openQuoteModal();
              }}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-medis-blue text-white px-5 py-3 rounded-md text-[14px] font-medium"
              data-testid="nav-mobile-cta-quote"
            >
              Get a Quote <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
